import { format } from 'date-fns'
import DOMPurify from 'dompurify'
import type { FC } from 'react'
import { useMemo, useEffect, useRef } from 'react'

import { Avatar, AvatarFallback, AvatarImage } from 'ui/avatar'

import type {
  ConversationThreadProps,
  ConversationTimelineItem,
  ConversationVisibility,
} from './types'
import { sortMessagesByDate, toCapitalize } from './utils'

interface TimelineGroup {
  id: string
  author: ConversationTimelineItem['author']
  visibility: ConversationVisibility
  isExternalReviewer: boolean
  items: ConversationTimelineItem[]
}

interface TimelineDayGroup {
  id: string
  label: string
  groups: TimelineGroup[]
}

const formatTime = (dateString: string): string => {
  if (!dateString) {
    return ''
  }

  try {
    return format(new Date(dateString), 'HH:mm')
  } catch {
    return ''
  }
}

const formatDateTime = (
  dateString: string,
  formatString: string = 'd MMM yyyy, HH:mm'
): string => {
  if (!dateString) {
    return ''
  }

  try {
    return format(new Date(dateString), formatString)
  } catch {
    return ''
  }
}

const buildGroupKey = (item: ConversationTimelineItem): string => {
  return `${item.author.id ?? item.author.name}-${item.visibility}`
}

const groupMessagesByAuthor = (
  messages: ConversationTimelineItem[]
): TimelineGroup[] => {
  return messages.reduce<TimelineGroup[]>((acc, message) => {
    const lastGroup = acc[acc.length - 1]

    const canJoinPreviousGroup = Boolean(
      lastGroup &&
      message.variant === 'note' &&
      lastGroup.items.every((item) => item.variant === 'note') &&
      buildGroupKey(lastGroup.items[0]) === buildGroupKey(message)
    )

    if (canJoinPreviousGroup) {
      acc[acc.length - 1] = {
        ...lastGroup,
        items: [...lastGroup.items, message],
      }
      return acc
    }

    return [
      ...acc,
      {
        id: message.id,
        author: message.author,
        visibility: message.visibility,
        isExternalReviewer: Boolean(message.isExternalReviewer),
        items: [message],
      },
    ]
  }, [])
}

const groupMessagesByDate = (
  messages: ConversationTimelineItem[]
): TimelineDayGroup[] => {
  if (messages.length === 0) {
    return []
  }

  const byDate = messages.reduce<Record<string, ConversationTimelineItem[]>>(
    (acc, message) => {
      const dateKey = format(new Date(message.createdAt), 'yyyy-MM-dd')
      if (!acc[dateKey]) {
        acc[dateKey] = []
      }

      acc[dateKey].push(message)
      return acc
    },
    {}
  )

  return Object.entries(byDate)
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .map(([key, items]) => {
      const label = format(new Date(items[0].createdAt), 'd MMM yyyy')
      return {
        id: key,
        label,
        groups: groupMessagesByAuthor(items),
      }
    })
}

const getBulletColor = (item: ConversationTimelineItem): string => {
  if (item.indicatorColor) {
    return item.indicatorColor
  }

  switch (item.variant) {
    case 'statusChange':
      return '#fa4f38'
    case 'postTypeChange':
      return '#6840f5'
    case 'note':
      return item.visibility === 'EXTERNAL' ? '#f6b324' : '#cbd5f5'
    case 'postCreated':
    default:
      return '#9ca3af'
  }
}

const renderStatusChange = (
  item: ConversationTimelineItem,
  formatText: ConversationThreadProps['formatText']
) => {
  const oldStatus = item.statusChange?.oldStatus?.label
  const newStatus = item.statusChange?.newStatus?.label
  const bulletColor = getBulletColor(item)

  return (
    <div>
      <div className="flex items-center gap-[8px] text-[12px] font-[500] leading-[16px] text-[#374151]">
        <div className="flex items-center justify-center w-[16px] h-[16px] p-[4px]">
          <i
            className="fa-solid fa-circle text-[8px]"
            style={{ color: bulletColor }}
            aria-hidden="true"
          />
        </div>
        <span className="text-[12px] font-[500] leading-[16px] text-[#454545]">
          Changed status:
        </span>
      </div>
      <div className="flex items-center gap-[8px] text-[12px] font-[700] leading-[18px] text-[#454545] ml-[25px]">
        <span>{oldStatus ?? '—'}</span>
        <i
          className="fa-solid fa-arrow-right text-[10px] text-[#808080]"
          aria-hidden="true"
        />
        <span>{newStatus ?? '—'}</span>
      </div>
      {item.comment && (
        <div className="mt-[4px] ml-[25px] rounded-[8px] bg-[#FFF8F8] p-[8px] text-[12px] leading-[16px] text-[#333]">
          <div className="font-[600] text-[#333]">Comment:</div>
          <div
            className="mt-[4px] text-[12px] leading-[16px] text-[#333] [&_a]:text-[#4f31ff] [&_a]:underline [&_a]:hover:text-[#6840f5]"
            dangerouslySetInnerHTML={{
              // SECURITY: DOMPurify must run AFTER formatText so any
              // collaborator-name HTML injected by the @[userId] expansion
              // is sanitised. The conversation/utils escapeHtml helper is
              // the primary defence; this ordering is the second.
              __html: DOMPurify.sanitize(formatText(item.comment)),
            }}
          />
        </div>
      )}
    </div>
  )
}

const renderPostCreated = (
  item: ConversationTimelineItem,
  formatText: ConversationThreadProps['formatText']
) => {
  const postType = item.postCreated?.postType
  const statusLabel = item.postCreated?.statusLabel || 'In progress'
  const fallback = item.text || ''
  // SECURITY: see escapeHtml in conversation/utils.ts. Order is
  // sanitise(format(x)) so collaborator-name expansion is sanitised.
  const formatted = fallback ? DOMPurify.sanitize(formatText(fallback)) : ''

  const postTypeText = postType ? toCapitalize(postType) : 'post'
  const statusText = statusLabel ? `${statusLabel}` : ''

  return (
    <div className="flex items-center gap-[4px] text-[12px] leading-[16px] text-[#454545]">
      <div className="flex items-center justify-center w-[16px] h-[16px] p-[4px]">
        <i
          className="fa-solid fa-circle text-[8px] text-[#b3b3b3]"
          aria-hidden="true"
        />
      </div>
      {fallback ? (
        <div
          className="[&_a]:text-[#4f31ff] [&_a]:underline [&_a]:hover:text-[#6840f5]"
          dangerouslySetInnerHTML={{
            __html: formatted,
          }}
        />
      ) : (
        <span>
          Created <strong>{postTypeText}</strong> as{' '}
          <strong>{statusText}</strong>
        </span>
      )}
    </div>
  )
}

const renderPostTypeChange = (item: ConversationTimelineItem) => {
  const { scheduleTime, oldPostType, newPostType } = item.postTypeChange || {}

  const scheduleText = scheduleTime
    ? `${formatDateTime(scheduleTime, 'EEE d MMM HH:mm aaa')}`
    : `${toCapitalize(oldPostType ?? '')} → ${toCapitalize(newPostType ?? '')}`

  const iconClass = scheduleTime
    ? 'fa-regular fa-alarm-clock'
    : 'fa-regular fa-file-lines'

  return (
    <div className="text-[12px] leading-[16px] text-[#454545]">
      <div className="flex items-center gap-[8px] font-[400] text-[#454545]">
        <div className="flex items-center justify-center w-[16px] h-[16px] p-[4px]">
          <i
            className={`${iconClass} text-[12px] text-[#4F31FF]`}
            aria-hidden="true"
          />
        </div>
        <span>{scheduleTime ? 'Schedule for:' : 'Post type updated'}</span>
      </div>
      {scheduleText && (
        <div className="mt-[4px] text-[#454545] ml-[25px] font-[700]">
          {scheduleText}
        </div>
      )}
    </div>
  )
}

const renderNote = (
  item: ConversationTimelineItem,
  isFirst: boolean,
  formatText: ConversationThreadProps['formatText']
) => {
  if (isFirst) {
    return (
      <div
        className="text-[14px] leading-[20px] text-[#333] font-[400] [&_a]:text-[#4f31ff] [&_a]:underline [&_a]:hover:text-[#6840f5]"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(formatText(item.text || '')),
        }}
      />
    )
  }

  return (
    <div className="flex gap-[12px]">
      <span className="min-w-[40px] text-[12px] leading-[16px] text-[#6B7280]">
        {formatTime(item.createdAt)}
      </span>
      <div
        className="flex-1 text-[13px] leading-[20px] text-[#1F2933] [&_a]:text-[#4f31ff] [&_a]:underline [&_a]:hover:text-[#6840f5]"
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(formatText(item.text || '')),
        }}
      />
    </div>
  )
}

const TimelineAvatar: FC<{
  name: string
  initials: string
  avatarUrl?: string | null
  visibility: ConversationVisibility
  isExternalNote: boolean
}> = ({ name, initials, avatarUrl, visibility, isExternalNote }) => {
  return (
    <div className="relative h-[36px] w-[36px]">
      <Avatar className="size-[32px]">
        {avatarUrl && (
          <AvatarImage src={avatarUrl} alt={name} className="object-cover" />
        )}
        <AvatarFallback className="bg-neutral-300 text-[10px] font-[400] uppercase text-white">
          {initials}
        </AvatarFallback>
      </Avatar>
      {visibility === 'EXTERNAL' && (
        <span className="absolute bottom-[2px] right-[2px] flex h-[12px] w-[12px] items-center justify-center rounded-[4px] bg-[#F59E0B] border border-[#ffffff]">
          <i
            className={`fa-regular ${isExternalNote ? 'fa-arrow-right' : 'fa-arrow-left'} text-[8px] text-[#ffffff]`}
            aria-hidden="true"
          />
        </span>
      )}
    </div>
  )
}

const TimelineGroupItem: FC<{
  group: TimelineGroup
  formatText: ConversationThreadProps['formatText']
  username?: string
}> = ({ group, formatText, username }) => {
  const firstItem = group.items[0]
  const isExternalNote =
    group.visibility === 'EXTERNAL' && group.items[0].variant === 'note'

  return (
    <li className="relative m-0 list-none">
      <div className="flex gap-[8px]">
        <TimelineAvatar
          isExternalNote={isExternalNote}
          name={group.author.name}
          initials={group.author.initials}
          avatarUrl={group.author.avatarUrl}
          visibility={group.visibility}
        />
        <div className="flex-1 font-[Inter] flex flex-col gap-[4px]">
          <div className="flex flex-wrap items-center gap-x-[4px]">
            <span className="text-[12px] font-[600] leading-[16px] text-[#333]">
              {group.author.name}
            </span>
            {group.visibility === 'EXTERNAL' &&
              username &&
              firstItem.metadata?.username === username && (
                <span className="text-[10px] leading-[14px] text-[#808080]">
                  (Yourself)
                </span>
              )}
            <span className="text-[10px] leading-[14px] text-[#808080]">
              {formatTime(firstItem.createdAt)}
            </span>
            {isExternalNote && (
              <span className="ml-auto rounded-[8px] bg-[#F59E0B] px-[8px] py-[0px] text-[10px] font-[500] leading-[14px] text-[#ffffff]">
                External note
              </span>
            )}
          </div>

          {group.items.map((item, index) => {
            // SKE-3798: non-comment activities sit in a grey-outlined box
            // (dashboard idea-activities pattern); notes/comments are borderless.
            const activityBox = 'rounded-lg border border-neutral-100 p-2'

            if (item.variant === 'statusChange') {
              return (
                <div key={item.id} className={activityBox}>
                  {renderStatusChange(item, formatText)}
                </div>
              )
            }

            if (item.variant === 'postCreated') {
              return (
                <div key={item.id} className={activityBox}>
                  {renderPostCreated(item, formatText)}
                </div>
              )
            }

            if (item.variant === 'postTypeChange') {
              return (
                <div key={item.id} className={activityBox}>
                  {renderPostTypeChange(item)}
                </div>
              )
            }

            return (
              <div key={item.id}>
                {renderNote(item, index === 0, formatText)}
              </div>
            )
          })}
        </div>
      </div>
    </li>
  )
}

const ConversationThread: FC<ConversationThreadProps> = ({
  messages,
  formatText,
  isReplied,
  isMobile,
  username,
}) => {
  const conversationRef = useRef<HTMLDivElement>(null)
  const sortedMessages = useMemo(() => sortMessagesByDate(messages), [messages])

  const dayGroups = useMemo(
    () => groupMessagesByDate(sortedMessages),
    [sortedMessages]
  )

  // Auto-scroll to bottom when messages are loaded
  useEffect(() => {
    if (conversationRef.current && messages.length > 0) {
      const element = conversationRef.current
      element.scrollTop = element.scrollHeight
    }
  }, [messages.length])

  // Desktop: fill the column's remaining height (flex child) so the list
  // scrolls inside it. Mobile keeps auto height (it lives in a collapse).
  const containerHeightClass = isMobile ? 'h-auto' : 'min-h-0 flex-1'

  return (
    <div className={`flex flex-col ${isMobile ? '' : 'min-h-0 flex-1'}`}>
      {isMobile && (
        <p className="mb-[16px] text-center text-[14px] font-[500] leading-[18px] text-[#374151]">
          {messages.length === 0
            ? 'No conversations yet'
            : 'View updates for this post.'}
        </p>
      )}
      <div
        id="conversation_wrap"
        ref={conversationRef}
        data-replied={isReplied}
        // border-t is full-bleed (touches the column edges); the horizontal
        // padding insets the content — comments, status changes and day
        // dividers — from the edges (mobile 16px / desktop 40px).
        className={`relative flex grow flex-col overflow-y-auto border-t border-[#e6e6e6] bg-[#FFFFFF] ${isMobile ? 'px-4' : 'px-10'} ${containerHeightClass}`}
      >
        {/* SKE-3807: borderless timeline — no container border, no per-entry
            cards (dashboard idea-planner idea-activities pattern). Dividing
            lines sit on the DAY dividers only, not between individual
            comments; entries within a day are separated by spacing. An
            edge-to-edge line (border-t on the wrap) sits above the whole list.
            The container has no horizontal padding so the day lines + top line
            bleed to both edges; each row carries its own px-[16px]. */}
        <ol className="flex flex-col list-none p-0 m-0">
          {dayGroups.length === 0 ? (
            <li className="py-[20px] text-center text-[13px] leading-[20px] text-[#6B7280]">
              No messages yet.
            </li>
          ) : (
            dayGroups.map((dayGroup) => (
              <li key={dayGroup.id} className="list-none">
                <div className="relative py-[12px] text-center text-[10px] font-[400] uppercase text-[#808080]">
                  <span className="relative z-10 rounded-[22px] border border-[#e6e6e6] bg-[#ffffff] px-[8px] py-[4px]">
                    {dayGroup.label}
                  </span>
                  <span
                    className="absolute left-0 top-[50%] h-[1px] w-full -translate-y-1/2 bg-[#e6e6e6]"
                    aria-hidden="true"
                  />
                </div>
                <ol className="m-0 flex list-none flex-col gap-[24px] p-0 pt-[8px] pb-[20px]">
                  {dayGroup.groups.map((group) => (
                    <TimelineGroupItem
                      key={`${dayGroup.id}-${group.id}`}
                      group={group}
                      formatText={formatText}
                      username={username}
                    />
                  ))}
                </ol>
              </li>
            ))
          )}
        </ol>
      </div>
    </div>
  )
}

export default ConversationThread
